import React, { useState, useEffect } from "react";
import {
  Menu,
  Settings,
  Minimize2,
  Maximize2,
  X,
  Mic,
  LogOutIcon,
  Bell,
  BellRing,
  Check,
  Clock,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  HelpCircle
} from "lucide-react";
import { useTranscriptionStore } from "../../store";
import LogoImg from "../../assets/images/logo512.png";
import LogoutModal from "../UI/LogoutModal";
import api from "../../config/axiosInstance";
import { handleLogout } from "../auth/authServices";
import { useNavigate, useRoutes } from "react-router-dom";

const Button = ({ children, variant = "default", size = "md", className = "", onClick, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground"
  };

  const sizes = {
    sm: "h-9 px-3 text-sm",
    md: "h-10 px-4 py-2",
    lg: "h-11 px-8"
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

const NotificationIcon = ({ type }) => {
  const iconProps = { size: 16 };

  switch (type) {
    case NOTIFICATION_TYPES.SUCCESS:
      return <CheckCircle {...iconProps} className="text-green-400" />;
    case NOTIFICATION_TYPES.ERROR:
      return <XCircle {...iconProps} className="text-red-400" />;
    case NOTIFICATION_TYPES.WARNING:
      return <AlertTriangle {...iconProps} className="text-yellow-400" />;
    case NOTIFICATION_TYPES.INFO:
    default:
      return <Info {...iconProps} className="text-blue-400" />;
  }
};

const formatTimeAgo = (timestamp) => {
  const now = new Date();
  const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

const NotificationPanel = ({ isOpen, onClose, notifications, onMarkAsRead, onMarkAllAsRead, pagination, setPagination, fetchNotification }) => {
  if (!isOpen) return null;


  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      <div className="fixed right-0 w-96 h-[calc(100vh)] bg-black/90 backdrop-blur-sm border-l border-white/10 z-50 flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Notifications</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllAsRead}
              className="text-white/70 hover:text-white text-xs"
            >
              Mark all read
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white/70 hover:text-white"
            >
              <X size={16} />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-white/50">
              <Bell size={48} className="mx-auto mb-2 opacity-30" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10 cursor-pointer">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`p-4 hover:bg-white/5 cursor-pointer transition-colors ${!notification.isRead ? 'bg-blue-500/10' : ''
                    }`}
                  onClick={() => onMarkAsRead(notification._id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <NotificationIcon type={notification.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-white truncate">
                          {notification.title}
                        </p>
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-white/70 mb-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2">
                        <Clock size={12} className="text-white/40" />
                        <span className="text-xs text-white/40">
                          {formatTimeAgo(notification.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {pagination?.page >= pagination.totalPages ? <></> : <div onClick={() => {
                setPagination({
                  ...pagination,
                  page: pagination.page + 1
                })

                fetchNotification(pagination.page + 1, true)
              }} className="p-3 text-center">Show More</div>}
            </div>
          )}
        </div>

      </div>
    </>
  );
};

const Header = () => {
  const { unAuthorizeUser, currentSession, closeCurrentSession, countUnread, setCountUnread } = useTranscriptionStore();

  const [notifications, setNotifications] = useState([]);
  const router = useNavigate()
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const userName = localStorage.getItem("name") || "User";
  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/api/v1/notifications/mark-read/${notificationId}`);
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notificationId ? { ...n, isRead: true } : n
        )
      );
      setCountUnread((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = () => {
    api.patch('/api/v1/notifications/mark-all-read').then(() => {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setCountUnread(0)
    }).catch((err) => {
      console.log(err)
    })
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const fetchNotification = async (page = 1, append = false) => {
    try {
      const res = await api.get(`/api/v1/notifications?page=${page}`);
      const notifications = res?.data?.data || [];

      setPagination(res?.data?.pagination);
      setCountUnread(res?.data?.countUnread);

      const mapped = notifications.map((i) => ({
        _id: i._id,
        type: NOTIFICATION_TYPES.INFO,
        title: i.title,
        message: i.content,
        timestamp: new Date(i.createdAt),
        isRead: i.readBy.includes(localStorage.getItem("userId")),
      }));

      setNotifications((prev) => {
        const updated = append ? [...prev, ...mapped] : mapped;
        return updated;
      });

    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };



  // useEffect(() => {
  //   if (isNotificationPanelOpen) {
  //     fetchNotification(1, false)
  //   }
  // }, [isNotificationPanelOpen])


  useEffect(() => {
    fetchNotification()
  }, [])


  const handleLogout2 = async () => {
    try {
      // Fire while still authenticated — must happen before the token is cleared below
      try {
        await api.post('/api/v1/login-activity/logout', { logoutType: 'manual' });
      } catch (activityError) {
        console.error("Failed to record logout activity:", activityError);
      }

      await unAuthorizeUser();

      await handleLogout()
      window.location.reload(true)
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setShowConfirm(false);
    }
  };

  return (
    <>
      <header className="h-16 bg-black/20 backdrop-blur-sm border-b border-white/10 flex items-center justify-between px-6 py-6 no-print">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center">
              <img
                src={LogoImg}
                style={{ width: "25px" }}
                className="object-contain"
                alt="logo"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                Radspeak AI
              </h1>
              <p className="text-sm text-white/60">
                Welcome, {userName}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
            className="text-white/70 hover:text-white relative"
          >
            {countUnread > 0 ? (
              <BellRing size={18} className="text-blue-400" />
            ) : (
              <Bell size={18} />
            )}
            {countUnread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {countUnread > 9 ? '9+' : countUnread}
              </span>
            )}
          </Button>

          {currentSession && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                try {
                  closeCurrentSession();
                } catch (error) { }
              }}
              className="text-white/70 hover:text-white"
            >
              <span style={{ marginTop: "-4px" }}> Close Case &nbsp; </span>
              <X size={18} />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => router('/help')}
            className="text-white/70 hover:text-white"
          >
            <span style={{ marginTop: "-4px" }}>Help&nbsp;</span>
            <HelpCircle size={18} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowConfirm(true)}
            className="text-white/70 hover:text-white"
          >
            <span style={{ marginTop: "-4px" }}>Logout&nbsp;</span>
            <LogOutIcon size={18} />
          </Button>

          <LogoutModal
            open={showConfirm}
            onConfirm={handleLogout2}
            onCancel={() => setShowConfirm(false)}
          />

          <div className="hidden desktop:flex items-center gap-1 ml-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white"
            >
              <Minimize2 size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white"
            >
              <Maximize2 size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-red-400"
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      </header>

      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        pagination={pagination}
        setPagination={setPagination}
        onClearAll={clearAllNotifications}
        fetchNotification={fetchNotification}
      />
    </>
  );
};

export default Header;